import { useEffect, useMemo, useState } from 'react'
import parse from 'html-react-parser'
import Image from 'next/image'
import { useRouter } from 'next/router'

import { Text } from '@wartek-id/text'
import { Button } from '@wartek-id/button-v1'
import { Spinner } from '@wartek-id/spinner'
import { useToast } from '@wartek-id/toast-v2'

import Header from 'app/BuktiKaryaPage/components/Header'
import { DocUploader } from '../blocks/DocUploader'
import {
  allSelectionDocumentTypes,
  useMutateAPIUploadSubmission,
  useQueryAPIGetKSSelectionCandidate,
  useQueryAPIGetVerificationDocuments,
} from 'api/bcks/useAPIBCKSInvitation'
import CustomFileUpload from '../blocks/CustomFileUpload'
import AndroidHandler from 'utils/androidHandler'
import SectionInfoGaruda from '../components/SectionInfoGaruda'

const docTitle = {
  PAKTA_INTEGRITAS: 'Pakta Integritas',
  HASIL_PENILAIAN_KINERJA: 'Hasil Penilaian Kinerja',
  SKCK: 'Surat Keterangan Catatan Kepolisian (masa berlaku 6 bulan)',
  SK_MANAJERIAL: 'SK Pengalaman Manajerial',
  SK_BEBAS_HUKUMAN: 'Surat Keterangan Bebas Hukuman Disiplin',
}

export const createFormData = (object: any): FormData => {
  const formData = new FormData()
  Object.keys(object).forEach((key) => formData.append(key, object[key]))

  return formData
}

export const BCKSInvitationRevisionPage = () => {
  const androidHandler = AndroidHandler()
  const toast = useToast()
  const router = useRouter()

  const { data: candidate, isLoading: isLoadingSelectionState } =
    useQueryAPIGetKSSelectionCandidate()
  const {
    data: verificationDocuments,
    isLoading: isLoadingVerificationDocuments,
  } = useQueryAPIGetVerificationDocuments(candidate?.selectionId, {
    enabled: candidate?.selectionCandidateState === 'documentVerification',
  })
  const { mutateAsync: uploadSubmission } = useMutateAPIUploadSubmission(
    candidate?.selectionId || ''
  )

  const [currentDocType, setCurrentDocType] = useState('')
  const [files, setFiles] = useState<{ [key: string]: File }>({})
  const [isSubmittingDocuments, setIsSubmittingDocuments] = useState(false)

  const totalRevisions = useMemo(
    () =>
      allSelectionDocumentTypes.filter(
        (t) => verificationDocuments?.[t]?.state === 'NEED_REVISION'
      ).length,
    [verificationDocuments]
  )

  const disableSubmit = Object.keys(files).length !== totalRevisions

  const handleCopyUrlClick = (urlToClipboard: string) => {
    if (androidHandler?.enabled) {
      androidHandler?.copyText(urlToClipboard)
      toast({
        message: 'Tautan berhasil disalin',
      })
      return
    }

    if (!navigator?.clipboard) {
      toast({
        message: 'Tautan gagal disalin',
      })
    }
    navigator.clipboard.writeText(urlToClipboard).then(
      function () {
        toast({
          message: 'Tautan berhasil disalin',
        })
      },
      function (err) {
        toast({
          message: `Tautan gagal disalin: ${err}`,
        })
      }
    )
  }

  const handleSelectDoc = (_, data) => {
    setFiles({ ...files, [currentDocType]: data.fileData })
    setCurrentDocType('')
  }

  const handleDeleteDoc = (docType: string) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [docType]: _, ...remainingFiles } = files
    setFiles(remainingFiles)
  }

  const handleUploadFiles = async () => {
    if (disableSubmit) return

    try {
      setIsSubmittingDocuments(true)
      await Promise.all(
        Object.entries(files).map(([fileType, file]) =>
          uploadSubmission(
            createFormData({
              selectionId: candidate?.selectionId,
              fileType,
              file,
            })
          )
        )
      )

      router.push('/bcks/invitation/final')
    } catch (error) {
      toast({
        message: 'Gagal mengunggah file',
      })
    } finally {
      setIsSubmittingDocuments(false)
    }
  }

  useEffect(() => {
    if (
      !!candidate &&
      (candidate.selectionCandidateState !== 'documentVerification' ||
        (!!verificationDocuments && totalRevisions <= 0))
    ) {
      router.push('/bcks/invitation')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidate])

  if (currentDocType) {
    return (
      <DocUploader
        selectedDocMeta={{ title: docTitle[currentDocType] }}
        docData={{ fileData: files[currentDocType] }}
        onDocUpload={handleSelectDoc}
        onBackClick={() => setCurrentDocType('')}
        isReviewMode
      />
    )
  }

  return (
    <div className="relative flex flex-col h-screen max-w-xl mx-auto">
      <Header title="Pengumpulan berkas" forceNavigateBack />

      <div className="bg-surface-selected-default p-4">
        <Text variant="body-sm">
          Catatan: Pastikan dokumen asli (<strong>bukan fotokopi</strong>),
          ukuran <strong>maks. 5 MB</strong>.
        </Text>
      </div>

      <div className="p-4">
        <section className="flex mb-4 p-4 border border-subdued rounded-lg md:hidden lg:hidden">
          <div className="w-36 mr-4">
            <Image
              layout="fixed"
              src="/images/img-typing-monitor.png"
              width={84}
              height={84}
              alt="Ilustrasi pengunggahan berkas"
            />
          </div>
          <div>
            <Text variant="body-sm" color="subdued">
              {parse(
                `Pengunggahan berkas dianjurkan melalui platform web Ruang GTK di <b>${window.location.host}</b>`
              )}
            </Text>

            <Button
              className="mt-4"
              color="black"
              size="sm"
              onClick={() => {
                handleCopyUrlClick(window.location.origin)
              }}
            >
              Salin tautan
            </Button>
          </div>
        </section>

        {(isLoadingSelectionState || isLoadingVerificationDocuments) && (
          <div className="flex justify-center my-6">
            <Spinner />
          </div>
        )}

        <div className="flex flex-col gap-5">
          {verificationDocuments?.hasilPenilaianKinerja.state ===
            'NEED_REVISION' && (
            <CustomFileUpload
              value={files['HASIL_PENILAIAN_KINERJA']}
              displayFileName="Hasil Penilaian Kinerja"
              title="Hasil Penilaian Kinerja"
              subtitle="Unggah hasil penilaian kinerja yang terbaru."
              exampleFilePath="/bcks/invitation/guide/kinerja"
              notes={verificationDocuments?.hasilPenilaianKinerja.reason}
              isLoading={isSubmittingDocuments}
              onClick={() => setCurrentDocType('HASIL_PENILAIAN_KINERJA')}
              onClickDelete={() => handleDeleteDoc('HASIL_PENILAIAN_KINERJA')}
            />
          )}

          {verificationDocuments?.catatanKepolisian.state ===
            'NEED_REVISION' && (
            <CustomFileUpload
              value={files['SKCK']}
              title="Surat Keterangan Catatan Kepolisian (masa berlaku 6 bulan)"
              displayFileName="SKCK"
              subtitle="Unggah SK perilaku baik dari Polres setempat dengan masa berlaku 6 bulan sejak tanggal diterbitkan."
              exampleFilePath="/bcks/invitation/guide/police"
              notes={verificationDocuments?.catatanKepolisian.reason}
              isLoading={isSubmittingDocuments}
              onClick={() => setCurrentDocType('SKCK')}
              onClickDelete={() => handleDeleteDoc('SKCK')}
            />
          )}

          {verificationDocuments?.pengalamanManajerial.state ===
            'NEED_REVISION' && (
            <CustomFileUpload
              value={files['SK_MANAJERIAL']}
              displayFileName="SK Manajerial"
              title="Surat Keterangan Pengalaman Manajerial"
              subtitle="Unggah surat keterangan atau keputusan dari Kepala Sekolah bahwa Anda memiliki 2 tahun pengalaman manajerial di satuan pendidikan, organisasi pendidikan, dan/atau komunitas pendidikan."
              extraDesc="*Khusus PPPK lampirkan juga bukti pengalaman kerja di dalam 1 dokumen yang sama"
              exampleFilePath="/bcks/invitation/guide/managerial"
              notes={verificationDocuments?.pengalamanManajerial.reason}
              isLoading={isSubmittingDocuments}
              onClick={() => setCurrentDocType('SK_MANAJERIAL')}
              onClickDelete={() => handleDeleteDoc('SK_MANAJERIAL')}
            />
          )}

          {verificationDocuments?.bebasHukuman.state === 'NEED_REVISION' && (
            <CustomFileUpload
              value={files['SK_BEBAS_HUKUMAN']}
              title="Surat Keterangan Bebas Hukuman Disiplin"
              displayFileName="SK Bebas Hukuman"
              subtitle="Unggah surat dari Kepala Sekolah bahwa Anda tidak pernah dijatuhi hukuman kedisplinan (tidak perlu materai)."
              exampleFilePath="/bcks/invitation/guide/discipline"
              notes={verificationDocuments?.bebasHukuman.reason}
              isLoading={isSubmittingDocuments}
              onClick={() => setCurrentDocType('SK_BEBAS_HUKUMAN')}
              onClickDelete={() => handleDeleteDoc('SK_BEBAS_HUKUMAN')}
            />
          )}

          {verificationDocuments?.paktaIntegritas?.state ===
            'NEED_REVISION' && (
            <CustomFileUpload
              value={files['PAKTA_INTEGRITAS']}
              title="Pakta Integritas"
              displayFileName="Pakta Integritas"
              subtitle="Unggah dokumen perjanjian yang ditandatangani."
              notes={verificationDocuments?.paktaIntegritas.reason}
              isLoading={isSubmittingDocuments}
              onClick={() => setCurrentDocType('PAKTA_INTEGRITAS')}
              onClickDelete={() => handleDeleteDoc('PAKTA_INTEGRITAS')}
            />
          )}
        </div>

        <SectionInfoGaruda className="my-6" />

        <Button
          fullWidth
          className="mt-6"
          size="lg"
          color="black"
          disabled={disableSubmit || isSubmittingDocuments}
          isLoading={isSubmittingDocuments}
          onClick={handleUploadFiles}
        >
          Kumpulkan
        </Button>
      </div>
    </div>
  )
}
